import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Image, Keyboard, ScrollView, Text, TouchableOpacity, View } from 'react-native-web';
import PropTypes from 'prop-types';
import Icon from 'react-native-vector-icons/dist/MaterialIcons';
import moment from 'moment';

// Utils
import { numberWithCommas } from '../utils/util_helpers';
import { showAlert } from '../utils/util_helpers';

// Configs
import GLOBAL from '../configs/config_global';
import { NavBar } from './component_nav_bar';
import { TextBox } from './component_text_box';
import { Button } from './component_button';
import LoadingView from './component_loading_view';
import { getFileName } from '../utils/util_files';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import { cloneDeep } from 'lodash';
import imageCompression from 'browser-image-compression';

export function CateringFeedbackForm(props, ref) {
    const {
        style,
        onSubmitFeedback,
        isSendingFeedback,
        givenFeedback,
        onFeedbackExpanded,
        isTrucksterLead
    } = props;

    // region Values
    const feedbackWeatherOptions = ['Good', 'Decent', 'Bad'];
    // endregion

    // region Refs
    const txtFeedbackSales = useRef(null);
    const txtInvoicingSales = useRef(null);
    const txtNotes = useRef(null);
    const photoPicker = useRef(null);
    // endregion

    // region Local State
    const [feedbackSales, setFeedbackSales] = useState('');
    const [invoicingSales, setInvoicingSales] = useState('');
    const [feedbackNotes, setFeedbackNotes] = useState('');
    const [feedbackWeatherIndex, setFeedbackWeatherIndex] = useState(0);
    const [feedbackPhotos, setFeedbackPhotos] = useState([]);
    const [promoCodeAmount, setPromoCodeAmount] = useState(0);
    const [feedbackGiven, setFeedbackGiven] = useState(false);
    const [visible, setVisible] = useState(false);
    const [photoCarouselOpened, setPhotoCarouselOpened] = useState(false);
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
    const [compressingPhotos, setCompressingPhotos] = useState(false);
    // endregion

    // region Component Mounting
    useEffect(() => {
        initializeFeedbackData();
    }, []);
    // endregion

    // region Function - Show/Hide Modal
    const expandForm = () => {
        setVisible(true);
        onFeedbackExpanded(true);
    }

    const collapseForm = () => {
        setVisible(false);
        onFeedbackExpanded(false);
    }
    // endregion

    // region Function - Initialize Feedback Data
    const initializeFeedbackData = (feedbackData = null) => {
        const usingFeedback = feedbackData || givenFeedback;
        const selectedWeatherIndex = feedbackWeatherOptions.findIndex((item) => item.toLowerCase() === usingFeedback.feedbackWeatherText?.toLowerCase());
        const newFeedbackPhotos = (
            usingFeedback.feedbackPhotos ?
                (
                    Array.isArray(usingFeedback.feedbackPhotos) ?
                        usingFeedback.feedbackPhotos
                        :
                        JSON.parse(usingFeedback.feedbackPhotos)
                )
                :
                []
        ).map((item, index) => ({ src: item.path, title: `Feedback Photo #${index + 1}` }));

        const promoCodeData = usingFeedback.promoCodeData || {};
        setFeedbackSales(usingFeedback.feedbackSales);
        setInvoicingSales(usingFeedback.invoicingSales);
        setFeedbackNotes(usingFeedback.feedbackNotes);
        setFeedbackWeatherIndex(selectedWeatherIndex > -1 ? selectedWeatherIndex : 0);
        setFeedbackPhotos(newFeedbackPhotos)
        setPromoCodeAmount(Object.keys(promoCodeData).length ? (Number(promoCodeData?.['total_discount'] || 0) / 100) : 0);
        setFeedbackGiven(Object.keys(usingFeedback).length > 1);
    };
    // endregion

    // region Expose Methods
    useImperativeHandle(
        ref,
        () => ({ expandForm, collapseForm, reloadForm: (newFeedbackData) => initializeFeedbackData(newFeedbackData) }),
        [givenFeedback]
    )
    // endregion

    // region Function - Go Submit Feedback
    const goSubmitFeedback = () => {
        if (isSendingFeedback) return;

        const salesCountStr = feedbackSales?.toString().trim().replace(/,/g, '').replace('$', '');
        const invoicingSalesCountStr = invoicingSales?.toString().trim().replace(/,/g, '').replace('$', '');
        const salesCount = Number(salesCountStr || 0);
        const invoicingSalesCount = Number(invoicingSalesCountStr || 0);

        if (!salesCountStr && (isTrucksterLead ? !invoicingSalesCountStr : false)) {
            showAlert({ text: 'Please enter your total (gross) sales for the event.<br/><br/>We use this for reporting and invoicing purposes only.' });
            return;
        }

        if (isTrucksterLead ? (!feedbackPhotos || feedbackPhotos.length === 0) : false) {
            showAlert({ text: 'Please provide a photo of your sales report' })
            return;
        }

        // Compress photos
        setCompressingPhotos(true);
        const imageCompressionOptions = { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true };
        Promise
            .all(
                feedbackPhotos
                    .map((item) => new Promise((resolve) => {
                        imageCompression(item.rawFile, imageCompressionOptions)
                            .then((compressedPhoto) => resolve(compressedPhoto))
                            .catch(() => resolve(null));
                    }))
                    .filter((item) => !!item)
            )
            .then((compressedPhotoArr) => {
                setCompressingPhotos(false);

                // Start submitting form
                showAlert({
                    text: 'Confirm submitting this feedback to the Truckster team?',
                    cancelButton: { text: 'Cancel' },
                    confirmButton: {
                        text: 'Confirm',
                        color: GLOBAL.COLOR.MAIN,
                        onPress: () => onSubmitFeedback({
                            salesCount,
                            invoicingSalesCount,
                            feedbackNotes: feedbackNotes,
                            feedbackWeather: feedbackWeatherOptions[feedbackWeatherIndex],
                            feedbackPhotos: compressedPhotoArr
                        })
                    }
                });
            });
    }
    // endregion

    // region Render Photo Item
    const renderPhotoItem = (item, index) => {
        const imageSize = 80;
        return(
            (item._id && item._id === -1) ?
                (
                    item.isFull ?
                        null
                        :
                        <TouchableOpacity
                            key={`photoItem_addButton`}
                            onPress={() => photoPicker.current?.click()}
                            style={{ borderWidth: 1, borderRadius: 6, borderStyle: 'dashed', borderColor: GLOBAL.COLOR.TEXT_LIGHT_GRAY, height: imageSize, width: imageSize, alignItems: 'center', justifyContent: 'center', marginRight: 16 }}>
                            <Icon name={'add-a-photo'} color={GLOBAL.COLOR.TEXT_GRAY} size={24} />

                            {/* Picker */}
                            <input
                                id={'logoPicker'}
                                type={'file'}
                                accept={'.png, .jpg, .jpeg'}
                                ref={photoPicker}
                                multiple={true}
                                style={{ display: 'none' }}
                                onChange={(event) => {
                                    event.stopPropagation();
                                    event.preventDefault();
                                    const fileList = Object.values(event.target.files || {}).map((file) => {
                                        const fileURL = URL.createObjectURL(file);
                                        return({
                                            rawFile: file,
                                            src: fileURL,
                                            isNew: true,
                                            uniqueName: `${moment().unix()}_${getFileName(fileURL)}`
                                        })
                                    });

                                    // Save to state
                                    setFeedbackPhotos(feedbackPhotos.concat(fileList.slice(0, 5 - feedbackPhotos.length)));
                                }}
                            />
                        </TouchableOpacity>
                )
                :
                <View
                    key={`photoItem_${item.uniqueName || item.id || getFileName(item.src)}`}
                    style={{ height: imageSize, width: imageSize, backgroundColor: GLOBAL.COLOR.TEXT_LIGHT_GRAY, borderRadius: 6, marginRight: 16, overflow: 'hidden' }}>
                    {/* Image Item */}
                    <TouchableOpacity
                        onPress={() => {
                            setCurrentPhotoIndex(index);
                            setPhotoCarouselOpened(true);
                        }}>
                        <Image
                            source={{ uri: item.src || item }}
                            style={{ width: imageSize, height: imageSize, backgroundColor: 'rgba(0, 0, 0, 0.1)' }}
                            resizeMode={'cover'}
                        />
                    </TouchableOpacity>

                    {/* Remove Button */}
                    {
                        (!isSendingFeedback && !feedbackGiven) &&
                        <TouchableOpacity
                            onPress={() => {
                                const newPhotoList = feedbackPhotos.filter((imageInState) => imageInState['uniqueName'] !== item['uniqueName']);
                                setFeedbackPhotos(newPhotoList);
                            }}
                            style={[
                                { position: 'absolute', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0, 0, 0, 0.7)' },
                                { top: 0, right: 0, width: 24, height: 24, zIndex: 5, borderBottomLeftRadius: 10 }
                            ]}>
                            <Icon
                                name={'clear'}
                                color={'white'}
                                size={12}
                            />
                        </TouchableOpacity>
                    }
                </View>
        );
    }
    // endregion

    // region Render Photo Section
    const renderPhotoSection = () => {
        const photoSectionPadding = 24;
        return(
            (feedbackGiven && (feedbackPhotos.length === 0)) ?
                <View style={{ width: '100%', alignItems: 'center', justifyContent: 'center', padding: photoSectionPadding }}>
                    <Text style={{ marginVertical: 24, fontSize: 13, color: GLOBAL.COLOR.TEXT_LIGHT_GRAY, textAlign: 'center' }}>
                        You didn't upload any photos to your feedback
                    </Text>
                </View>
                :
                <View style={{ width: '100%', flexDirection: 'row', alignItems: 'flex-start', padding: photoSectionPadding }}>
                    {/* Photo List */}
                    {
                        ((isSendingFeedback || feedbackGiven) ? [] : [{ _id: -1, isFull: feedbackPhotos.length >= 5 }])
                            .concat(feedbackPhotos)
                            .map((item, index) => renderPhotoItem(item, index))
                    }

                    {/* Lightbox Carousel */}
                    <Lightbox
                        plugins={[Captions]}
                        open={photoCarouselOpened}
                        close={() => setPhotoCarouselOpened(false)}
                        index={currentPhotoIndex}
                        on={{ view: ({ index }) => setCurrentPhotoIndex(index) }}
                        slides={feedbackPhotos.map((item, index) => Object.assign(cloneDeep(item), { title: `Feedback Photo #${index + 1}` }))}
                        carousel={{ imageFit: 'contain' }}
                        styles={{ container: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
                    />
                </View>
        );
    }
    // endregion

    // region Render Active Feedback Content
    const renderActiveContent = () => {
        const promoAmount = Number(promoCodeAmount);
        const enteredSale = feedbackGiven ? `$${numberWithCommas(feedbackSales, false, 2)}` : (feedbackSales || '').toString();
        const enteredActualSale = feedbackGiven ? `$${numberWithCommas(invoicingSales, false, 2)}` : (invoicingSales || '').toString();
        return(
            <View style={{ width: '100%' }}>
                {/* Nav Bar */}
                <NavBar
                    title={feedbackGiven ? 'Submitted Feedback' : 'Submit Feedback'}
                    leftButtonOnPress={() => collapseForm()}
                />

                {/* Content */}
                <View style={{ flex: 1, width: '100%' }}>
                    {/* Text Boxes */}
                    <View style={{ width: '100%', flex: 1 }}>
                        <ScrollView style={{ width: '100%', flex: 1 }} showsVerticalScrollIndicator={false}>
                            <View style={{ width: '100%', padding: 24, paddingBottom: 0 }}>
                                {/* Promo Code Container */}
                                {
                                    (promoAmount > 0) &&
                                    <>
                                        {/* Promo Code Amount */}
                                        <Text style={{ fontWeight: 'bold', color: GLOBAL.COLOR.BASIC.UI_BLUE }}>
                                            Truckster Ordering promo code amount
                                        </Text>

                                        {/* Promo Code Value */}
                                        <TextBox
                                            title={'Amount'}
                                            containerStyle={{ alignSelf: 'stretch', marginTop: 4 }}
                                            value={`$${numberWithCommas(promoAmount, false, 2)}`}
                                            disabled={true}
                                        />
                                    </>
                                }

                                {/* Sales */}
                                <Text style={{ fontWeight: 'bold', marginTop: promoAmount > 0 ? 32 : 0 }}>
                                    Your total sales for the event <Text style={{ color: GLOBAL.COLOR.BASIC.RED }}>*</Text>
                                </Text>

                                {/* Sales Input */}
                                <View style={{ alignSelf: 'stretch', flexDirection: 'row', alignItems: 'center', marginTop: 4 }}>
                                    {/* Text Input */}
                                    <TextBox
                                        ref={txtFeedbackSales}
                                        title={'Gross Sales'}
                                        placeholder={'$0'}
                                        returnKeyType="next"
                                        autoCapitalize="none"
                                        keyboardType={'numeric'}
                                        autoCorrect={false}
                                        style={{ flex: 1 }}
                                        onChangeText={(text) => setFeedbackSales((text ? '$' : '') + text.replace('$', '').toString().replace(/,/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ','))}
                                        onSubmitEditing={() => txtInvoicingSales.current?.focus()}
                                        value={enteredSale}
                                        disabled={feedbackGiven}
                                    />

                                    {/* Promo Code Amount */}
                                    {
                                        ((promoAmount > 0) && !!enteredSale.length) &&
                                        <View style={{ flexDirection: 'row', alignItems: 'center', marginLeft: 8, marginTop: 2 }}>
                                            <Text style={{ fontWeight: 'bold', color: GLOBAL.COLOR.BASIC.UI_BLUE }}>
                                                + ${promoAmount.toFixed(2)}
                                            </Text>
                                            <Text>{'  =  '}</Text>
                                            <Text style={{ fontWeight: 'bold' }}>
                                                $
                                                {
                                                    numberWithCommas(
                                                        Number(enteredSale.replace('$', '').replaceAll(',', '')) + Number(promoAmount),
                                                        false,
                                                        2
                                                    )
                                                }
                                            </Text>
                                        </View>
                                    }
                                </View>

                                {/* Sales */}
                                <Text style={{ fontWeight: 'bold', marginTop: 32 }}>
                                    Your sales, excluding tips & taxes, for the event {isTrucksterLead && <Text style={{ color: GLOBAL.COLOR.BASIC.RED }}>*</Text>}
                                </Text>

                                {/* Actual Sales Input */}
                                <View style={{ alignSelf: 'stretch', flexDirection: 'row', alignItems: 'center', marginTop: 4 }}>
                                    <TextBox
                                        ref={txtInvoicingSales}
                                        title={'Net Sales'}
                                        placeholder={'$0'}
                                        returnKeyType="next"
                                        autoCapitalize="none"
                                        keyboardType={'numeric'}
                                        autoCorrect={false}
                                        style={{ flex: 1 }}
                                        onChangeText={(text) => setInvoicingSales((text ? '$' : '') + text.replace('$', '').toString().replace(/,/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ','))}
                                        onSubmitEditing={() => txtNotes.current?.focus()}
                                        value={enteredActualSale}
                                        disabled={feedbackGiven}
                                    />

                                    {/* Promo Code Amount */}
                                    {
                                        ((promoAmount > 0) && !!enteredActualSale.length) &&
                                        <View style={{ flexDirection: 'row', alignItems: 'center', marginLeft: 8, marginTop: 2 }}>
                                            <Text style={{ fontWeight: 'bold', color: GLOBAL.COLOR.BASIC.UI_BLUE }}>
                                                + ${promoAmount.toFixed(2)}
                                            </Text>
                                            <Text>{'  =  '}</Text>
                                            <Text style={{ fontWeight: 'bold' }}>
                                                $
                                                {
                                                    numberWithCommas(
                                                        Number(enteredActualSale.replace('$', '').replaceAll(',', '')) + Number(promoAmount),
                                                        false,
                                                        2
                                                    )
                                                }
                                            </Text>
                                        </View>
                                    }
                                </View>

                                {/* Sales */}
                                <Text style={{ fontWeight: 'bold', marginTop: 32 }}>
                                    Overall feedback for the event
                                </Text>

                                {/* Sales Input */}
                                <TextBox
                                    ref={txtNotes}
                                    title={'Your Feedback'}
                                    placeholder={'Your thoughts on the event'}
                                    autoCorrect={false}
                                    containerStyle={{ alignSelf: 'stretch', marginTop: 4 }}
                                    onChangeText={(text) => setFeedbackNotes(text)}
                                    onSubmitEditing={() => Keyboard.dismiss()}
                                    value={feedbackNotes}
                                    disabled={feedbackGiven}
                                />

                                {/* Weather */}
                                <Text style={{ fontWeight: 'bold', marginTop: 32 }}>
                                    How was the weather?
                                </Text>

                                {/* Weather Options */}
                                <View
                                    pointerEvents={feedbackGiven ? 'none' : 'auto'}
                                    style={{ flexDirection: 'row', alignItems: 'center', alignSelf: 'stretch', marginTop: 16 }}>
                                    {feedbackWeatherOptions.map((item, index) => {
                                        let isSelected = index === feedbackWeatherIndex;
                                        return(
                                            <TouchableOpacity
                                                key={`weatherButton_${item}`}
                                                onPress={() => setFeedbackWeatherIndex(index)}
                                                style={{
                                                    width: 100, paddingHorizontal: 16, paddingVertical: 12, borderRadius: 6,
                                                    backgroundColor: isSelected ? GLOBAL.COLOR.BASIC.UI_BLUE : GLOBAL.COLOR.BACKGROUND_GREY,
                                                    alignItems: 'center', marginHorizontal: index === 1 ? 8 : 0
                                                }}>
                                                <Text style={{ color: isSelected ? 'white' : 'black', fontWeight: isSelected ? '500' : '400', fontSize: 16 }}>
                                                    {item}
                                                </Text>
                                            </TouchableOpacity>
                                        );
                                    })}
                                </View>

                                {
                                    isTrucksterLead &&
                                    <>
                                        {/* Photos */}
                                        <Text style={{ fontWeight: 'bold', marginTop: 32 }}>
                                            Photos (Max 5)
                                        </Text>

                                        <Text style={{ color: GLOBAL.COLOR.TEXT_GRAY, fontWeight: '500', fontSize: 16, marginTop: 12 }}>
                                            Sales report required <Text style={{ color: GLOBAL.COLOR.BASIC.RED }}>*</Text>
                                        </Text>

                                        <Text style={{ marginTop: 12, color: GLOBAL.COLOR.BASIC.NEUTRAL_GREY, fontSize: 16, lineHeight: 22 }}>
                                            Upload a photo of your sales report, fun moments that you had at the event or any incident that might have happened
                                        </Text>
                                    </>
                                }
                            </View>

                            {/* Photo Section */}
                            {isTrucksterLead && renderPhotoSection()}
                        </ScrollView>
                    </View>

                    {/* Buttons */}
                    <View style={{ flexDirection: 'row', alignItems: 'center', width: '100%', paddingHorizontal: 24, paddingTop: 8 }}>
                        {
                            !isSendingFeedback &&
                            <Button
                                onPress={() => collapseForm()}
                                style={{ backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY, flex: 1, marginRight: 4 }}
                                text={feedbackGiven ? 'Close' : `Do Later`}
                                textColor={'black'}
                                textStyle={{ letterSpacing: 0.5, fontWeight: 'bold' }}
                            />
                        }

                        {
                            !feedbackGiven &&
                            <Button
                                onPress={() => goSubmitFeedback()}
                                style={{ backgroundColor: GLOBAL.COLOR.MAIN, flex: 1, marginLeft: 4 }}
                                text={`Submit`}
                                textColor={'white'}
                                textStyle={{ letterSpacing: 0.5, fontWeight: 'bold' }}
                                loadingText={'Submitting Feedback'}
                                isLoading={isSendingFeedback}
                            />
                        }
                    </View>
                </View>
            </View>
        );
    }
    // endregion

    // region MAIN RENDER
    return(
        <View
            style={[{ width: '100%', alignSelf: 'center', backgroundColor: 'white', paddingBottom: 32 }].concat(style)}>
            {
                visible ?
                    renderActiveContent()
                    :
                    <View style={{ width: '100%', alignSelf: 'center', alignItems: 'center', paddingHorizontal: 64, paddingTop: 32 }}>
                        {/* Instruction Text */}
                        <Text style={{ color: GLOBAL.COLOR.BASIC.NEUTRAL_GREY, marginBottom: 24, textAlign: 'center', alignSelf: 'stretch', fontWeight: '500', lineHeight: 28 }}>
                            {
                                feedbackGiven ?
                                    'Thank you for giving us your feedback,\myou can see it again below.'
                                    :
                                    'Congratulations on completing your catering event,\nlet us know how it went!'
                            }
                        </Text>

                        {/* Button */}
                        <Button
                            onPress={() => expandForm()}
                            style={{ backgroundColor: feedbackGiven ? GLOBAL.COLOR.BASIC.UI_BLUE : GLOBAL.COLOR.MAIN, flex: 1 }}
                            text={feedbackGiven ? 'View Your Feedback' : 'Start Feedback'}
                            textColor={'white'}
                            textStyle={{ letterSpacing: 0.5, fontWeight: 'bold' }}
                        />
                    </View>
            }

            {/* Loading Cover */}
            {(compressingPhotos || isSendingFeedback) && <LoadingView isAbsolute={true} description={compressingPhotos ? 'Compressing photos' : 'Submitting your feedback'}/>}
        </View>
    )
    // endregion
}

CateringFeedbackForm = React.forwardRef(CateringFeedbackForm);

CateringFeedbackForm.propTypes = {
    style: PropTypes.object,
    onSubmitFeedback: PropTypes.func,
    isSendingFeedback: PropTypes.bool,
    givenFeedback: PropTypes.object,
    onFeedbackExpanded: PropTypes.func,
    isTrucksterLead: PropTypes.bool
}

CateringFeedbackForm.defaultProps = {
    style: {},
    onSubmitFeedback: () => {},
    isSendingFeedback: false,
    givenFeedback: {},
    onFeedbackExpanded: () => {},
    isTrucksterLead: true
}
